import { apiMixins } from "./api";

const FILE_ENDPOINT = "item/file";

function normalizeSubPath(subPath) {
  if (typeof subPath === 'string') {
    return subPath.replace(/\\/g, '/').split('/').filter((p) => p);
  }
  return subPath;
}

function createFileRequestBody({ parentItem, name, subPath, retentionSchedule, size, file, isSecret, contentType, tags, metadata, uploadJobId, uploadJobLogId, clientEntryId, overwriteExisting, redsMetadata }) {
  const body = {
    parentId: parentItem.id,
    name,
    subpath: normalizeSubPath(subPath),
    retentionSchedule,
    size: size ?? file?.size ?? 0,
    isSecret: isSecret ?? false,
    fileMimeType: contentType ?? file?.type,
    tags,
    metadata,
  };
  if (redsMetadata) {
    body.redsMetadata = redsMetadata;
  }
  if (uploadJobId || uploadJobLogId || clientEntryId) {
    body.uploadJobId = uploadJobId;
    body.uploadJobLogId = uploadJobLogId;
    body.clientEntryId = clientEntryId;
    if (overwriteExisting === true) {
      body.overwriteExisting = true;
    }
  }
  return body;
}

function normalizeUploadUrl(data = {}) {
  const rawUrl = data.uploadUrl ?? data.url;
  if (!rawUrl) {
    return null;
  }
  return typeof rawUrl === "string" ? rawUrl : rawUrl.toString?.() || null;
}

function normalizeUploadFileResponse(data = {}) {
  return {
    id: data.id,
    uploadUrl: normalizeUploadUrl(data),
    uploadId: data.uploadId,
    sensitiveNameDetection: data.sensitiveNameDetection,
    chunkedUploadRequired: !!data.chunkedUploadRequired,
    crcVerificationRequired: !!data.crcVerificationRequired,
  };
}

export const fileMixins = {
  mixins: [apiMixins],
  methods: {
    async renameFile(item, name) {
      await this.callAPI(`${FILE_ENDPOINT}/${item.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
    },
    async moveFile(item, parentItem) {
      await this.callAPI(`${FILE_ENDPOINT}/${item.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parentId: parentItem.id }),
      });
    },
    async deleteFile(item, { showLoader = true } = {}) {
      await this.callAPI(`${FILE_ENDPOINT}/${item.id}`, { method: "DELETE", showLoader });
    },
    async getFileDownloadUrl(item) {
      const data = await this.callAPI('item/download-token', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: [item.id] }),
      });
      return data.urls[item.id];
    },
    async createFile(options) {
      const body = createFileRequestBody(options);
      const uploadJobContext = !!(body.uploadJobId || body.uploadJobLogId || body.clientEntryId);
      const data = await this.callAPI(FILE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        showLoader: !uploadJobContext,
        body: JSON.stringify(body),
      });
      return normalizeUploadFileResponse(data);
    },
    async createFilesBatch(files) {
      const requestFiles = (files || []).map((options) => createFileRequestBody(options));
      const data = await this.callAPI(`${FILE_ENDPOINT}/upload-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        showLoader: false,
        body: JSON.stringify({ files: requestFiles }),
      });
      return {
        entries: (data?.entries || []).map((entry) => ({
          clientEntryId: entry.clientEntryId,
          uploadJobLogId: entry.uploadJobLogId,
          success: entry.success !== false,
          errorCode: entry.errorCode,
          errorMessage: entry.errorMessage,
          crcVerificationRequired: !!entry.crcVerificationRequired,
          ...normalizeUploadFileResponse(entry),
        })),
      };
    },
    async finaliseFilesBatch(entries = []) {
      return await this.callAPI(`${FILE_ENDPOINT}/finalise-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        showLoader: false,
        body: JSON.stringify({ entries }),
      });
    },
    async finaliseFile({ id, uploadJobId, uploadJobLogId, expectedCrc32c } = {}) {
      const options = { method: "POST", showLoader: false };
      if (uploadJobId || uploadJobLogId || expectedCrc32c) {
        options.headers = { "Content-Type": "application/json" };
        options.body = JSON.stringify({ uploadJobId, uploadJobLogId, expectedCrc32c });
      }
      await this.callAPI(FILE_ENDPOINT + "/" + id + "/finalise", options);
    },
    async getExpiredFiles({ page = 0, pageSize = 0, sort, showLoader = true } = {}){
      return await this.callAPI(`${FILE_ENDPOINT}/expired`, {
        method: "GET",
        query: {page, pageSize,sort},
        showLoader,
      });
    },
    async getFileVersions(itemId, { page = 0, pageSize = 10, sort = "createdOn,desc", showLoader = true } = {}) {
      return await this.callAPI(`${FILE_ENDPOINT}/${itemId}/versions`, {
        method: "GET",
        query: { page, size: pageSize, sort },
        showLoader,
      });
    },
    async getFileAccessHistory(itemId, { page = 0, pageSize = 10, sort = "requestedOn,desc", showLoader = true } = {}) {
      return await this.callAPI(`${FILE_ENDPOINT}/${itemId}/access-history`, {
        method: "GET",
        query: { page, size: pageSize, sort },
        showLoader,
      });
    },
    async getFileVersionDownloadUrl(itemId, version) {
      return await this.callAPI(`${FILE_ENDPOINT}/${itemId}/versions/${version}/download`, {
        method: "GET",
        showLoader: false,
      });
    },
    async restoreFileVersion(itemId, version) {
      return await this.callAPI(`${FILE_ENDPOINT}/${itemId}/versions/${version}/restore`, {
        method: "POST",
        showLoader: false,
      });
    },
    async refreshIndex(itemId, { fast = false } = {}) {
      return await this.callAPI(`index/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId, fast }),
        showLoader: false,
      });
    },
    async updateFile(item, {suspensionOrders, retentionSchedule, isSecret, steward, owner, tags, retainHistoric, metadata }) {
      let suspensionOrdersList = null;
      if (suspensionOrders || suspensionOrders === '') {
        suspensionOrdersList = suspensionOrders.length === 0 ? [] : suspensionOrders.split(",");
      }
      await this.callAPI(`${FILE_ENDPOINT}/${item.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ suspensionOrders: suspensionOrdersList, retentionSchedule, isSecret, steward, owner, tags, retainHistoric, metadata }),
      });
      try {
        return await this.callAPI(`${FILE_ENDPOINT}/${item.id}`, {
          method: "GET",
          showLoader: false,
        });
      } catch (error) {
        if (error?.code === "ACCESS_DENIED") {
          return null;
        }
        throw error;
      }
    }
  },
};
